import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { auditLogApi } from '../services/api';

interface ActivityLog {
  id: number;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
  action_type: string;
  timestamp: string;
  content_type: {
    id: number;
    app_label: string;
    model: string;
  };
  object_id: number;
  content_object_str: string;
  details: any;
}

interface AuditLogViewerProps {
  open: boolean;
  onClose: () => void;
  appName: string;
  modelName: string;
  objectId: number;
  title?: string;
}

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({
  open,
  onClose,
  appName,
  modelName,
  objectId,
  title,
}) => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (open && appName && modelName && objectId) {
      fetchAuditLogs();
    }
  }, [open, appName, modelName, objectId]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditLogApi.getForObject(appName, modelName, objectId);
      setLogs(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch audit logs');
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLogs([]);
    setError(null);
    setSelectedLog(null);
    setDetailsOpen(false);
    onClose();
  };

  const formatActionType = (actionType: string) => {
    return actionType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getUserDisplay = (user: ActivityLog['user']) => {
    if (!user) return 'System';
    return `${user.first_name} ${user.last_name}`.trim() || user.username;
  };

  const getActionColor = (actionType: string) => {
    const type = actionType.toLowerCase();
    if (type.includes('create')) return 'success';
    if (type.includes('update') || type.includes('edit')) return 'info';
    if (type.includes('delete') || type.includes('remove')) return 'error';
    if (type.includes('initialize') || type.includes('fetch')) return 'primary';
    return 'default';
  };

  const handleShowDetails = (log: ActivityLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedLog(null);
    setDetailsOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {title || `Audit Log - ${modelName} #${objectId}`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : logs.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="body1" color="text.secondary">
                No audit logs found for this {modelName.toLowerCase()}.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2" component="div">
                          {format(new Date(log.timestamp), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(log.timestamp), 'HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatActionType(log.action_type)}
                          size="small"
                          color={getActionColor(log.action_type) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getUserDisplay(log.user)}
                        </Typography>
                        {log.user && (
                          <Typography variant="caption" color="text.secondary">
                            {log.user.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatActionType(log.action_type)} on {log.content_object_str}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {log.details && Object.keys(log.details).length > 0 && (
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleShowDetails(log)}
                              color="primary"
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleDetailsClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Action Details
            </Typography>
            <IconButton onClick={handleDetailsClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedLog && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {formatActionType(selectedLog.action_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Performed by {getUserDisplay(selectedLog.user)} on{' '}
                {format(new Date(selectedLog.timestamp), 'MMM dd, yyyy HH:mm:ss')}
              </Typography>
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Details:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    <pre style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace'
                    }}>
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleDetailsClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuditLogViewer;
